import React, { useCallback, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import SearchInput from "@/components/ui/SearchInput";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";
import { ChevronDown, ChevronUp, ChevronRight, Download } from "lucide-react";
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter";
import { fetchDirectTable, fetchDirectFilter, DirectCallJourneyRow, DirectRoleScope } from "@/lib/axios/api/widgets.api";
import { formatDuration, formatDirectionForDisplay } from "@/components/widgets/users/tableCellHelpers";
import { useColumnFilterState, ColFilterPopover } from "@/components/widgets/users/columnFilterShared";
import Timeline from "@/components/widgets/call-overview/Timeline";
import { getPresenceColor, getDirectionColor } from "@/components/widgets/call-overview/callStatusColors";
import {
  TABLE_CARD_CLASS,
  TABLE_HEADER_ROW_COLOR_CLASS,
  TABLE_HEADER_TITLE_CLASS,
  TableGridHeaderRow,
  TableGridHeaderCell,
  TableGridRow,
  TableGridCell,
  type TableColumnConfig,
} from "@/components/ui/table";
import * as XLSX from "xlsx";

function capitalizeFirst(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function buildColumnFiltersPayload(cf: Record<string, string[]>) {
  const out = Object.entries(cf)
    .filter(([, values]) => values.length > 0)
    .map(([column, values]) => ({ column, values }));
  return out.length ? out : null;
}

function isAbortError(e: unknown): boolean {
  return e instanceof Error && (e.name === "CanceledError" || e.name === "AbortError");
}

const SEARCH_MAIN_COLUMN = "agentName";
const FILTER_PAGE_SIZE = 50;

const BASE_COLUMNS: TableColumnConfig[] = [
  { title: "Start Time", key: "startTime", width: 170, filterable: true },
  { title: "Organizer", key: "organizer", width: 160, filterable: true },
  { title: "Participant", key: "participant", width: 220, filterable: true },
  { title: "Direction", key: "direction", width: 130, filterable: true },
  { title: "Call Status", key: "callStatus", width: 120, filterable: true },
  { title: "Call Duration", key: "callDuration", width: 100, filterable: true },
  { title: "Customer Name", key: "customerName", width: 160, filterable: true },
  { title: "Company Name", key: "companyName", width: 160, filterable: true },
  { title: "Voicemail", key: "voicemail", width: 100, filterable: true },
];

/** TCX-6406: Customer Name / Company Name are gated behind the backend's runtime toggle. */
const CUSTOMER_COMPANY_NAME_KEYS = new Set(["customerName", "companyName"]);

const FILTERABLE_COLUMN_KEYS = BASE_COLUMNS.filter((c) => c.filterable).map((c) => c.key);

// The filter endpoint only understands single-value `{ column, value }` clauses — no `values`
// arrays. A column with multiple selected values is joined into one comma-separated string
// (matches the convention already used for /user/filter's appliedApiFilters), so a multi-select
// on another column still narrows this column's distinct list instead of being dropped. Every
// other filterable column is always included — `value: null` for columns with no active filter —
// so the backend knows the full column set, not just the ones currently narrowed.
function getOtherColumnFilters(columnFilters: Record<string, string[]>, col: string) {
  const out = FILTERABLE_COLUMN_KEYS
    .filter((k) => k !== col)
    .map((column) => {
      const values = columnFilters[column] ?? [];
      return { column, value: values.length > 0 ? values.join(', ') : null };
    });
  return out.length ? out : null;
}

// Export order differs from the on-screen table: Waiting/Talking Duration are inserted right
// before Call Duration, with Voicemail last.
const EXPORT_COLUMNS = [
  { title: "Start Time", key: "startTime" },
  { title: "Organizer", key: "organizer" },
  { title: "Participant", key: "participant" },
  { title: "Direction", key: "direction" },
  { title: "Call Status", key: "callStatus" },
  { title: "Waiting Duration", key: "waitingDuration" },
  { title: "Talking Duration", key: "talkingDuration" },
  { title: "Call Duration", key: "callDuration" },
  // TCX-6406 Amendment: Customer Name / Company Name are shown on-screen but excluded from export.
  { title: "Voicemail", key: "voicemail" },
];

interface DirectCallJourneyTableWidgetProps {
  startDate?: string;
  endDate?: string;
  /**
   * TCX-6503: the Manager's role scope. Narrows which calls exist; never switches a calculation
   * rule. Sent at request level, separate from the manual selection below. Omitted = unrestricted.
   */
  roleScope?: DirectRoleScope;
  /** The user's manual filter selection only. `null`/empty means "no manual narrowing". */
  manualAgentIds?: string[] | null;
  // Bumped by a with-agents-and-role poll detecting a real change (e.g. an agent renamed) —
  // id lists alone don't change on a rename, so this forces a refetch even when queryKey's
  // id-based portion is unchanged (mirrors UserPerformanceTableWidget's fix).
  queuesDataVersion?: number;
}

const NO_ROLE_SCOPE: DirectRoleScope = { queueIds: [], agentIds: [], directAgentIds: [] };

const DirectCallJourneyTableWidget: React.FC<DirectCallJourneyTableWidgetProps> = ({
  startDate,
  endDate,
  roleScope = NO_ROLE_SCOPE,
  manualAgentIds = null,
  queuesDataVersion = 0,
}) => {
  const [sortKey, setSortKey] = useState<string>("startTime");
  const [sortDir, setSortDir] = useState<"Asc" | "Desc">("Desc");
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [tableData, setTableData] = useState<DirectCallJourneyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [customerCompanyNameEnabled, setCustomerCompanyNameEnabled] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const lastQueryKeyRef = React.useRef("");
  const filterParamsKeyRef = React.useRef<Record<string, string>>({});
  const skeletonRowIds = useMemo(() => Array.from({ length: limit }, () => crypto.randomUUID()), [limit]);
  const columns = useMemo(
    () => (customerCompanyNameEnabled ? BASE_COLUMNS : BASE_COLUMNS.filter((c) => !CUSTOMER_COMPANY_NAME_KEYS.has(c.key))),
    [customerCompanyNameEnabled]
  );

  const colFilter = useColumnFilterState();

  // ── Table data fetch ──────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!startDate || !endDate) {
      setTableData([]);
      lastQueryKeyRef.current = "";
      return;
    }
    const colFiltersKey = JSON.stringify(columnFilters);
    const agentKey = (manualAgentIds ?? []).join(",");
    // TCX-6503: role scope is part of the query identity too — when a Manager's queues or groups
    // change, the same manual filter must still re-fetch against the new scope.
    const roleKey = `${roleScope.queueIds.join(",")}|${roleScope.agentIds.join(",")}|${roleScope.directAgentIds.join(",")}`;
    const queryKey = `${startDate}|${endDate}|${roleKey}|${agentKey}|${sortKey}|${sortDir}|${page}|${limit}|${colFiltersKey}|${search}|${queuesDataVersion}`;

    const controller = new AbortController();
    const run = async () => {
      if (queryKey === lastQueryKeyRef.current) return;
      lastQueryKeyRef.current = queryKey;
      setLoading(true);
      setError("");
      try {
        const result = await fetchDirectTable(
          {
            startTime: startDate,
            endTime: endDate,
            roleScope,
            manualAgentIds: manualAgentIds ?? null,
            sorts: sortKey ? [{ column: sortKey, direction: sortDir }] : null,
            pageIndex: page - 1,
            pageSize: limit,
            filters: buildColumnFiltersPayload(columnFilters),
            mainFilters: search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null,
          },
          { signal: controller.signal }
        );
        setTableData(result.data);
        setTotal(result.total);
        setCustomerCompanyNameEnabled(result.customerCompanyNameEnabled);
      } catch (e) {
        if (isAbortError(e)) return;
        setError(e instanceof Error ? e.message : "Error loading data");
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };
    run();

    return () => controller.abort();
    // `roleScope` must be a dep: DirectCallTab memoizes `manualAgentIds` by reference, so a
    // Manager's queue/group scope changing alone (same manual filter) previously never re-ran
    // this effect at all — the queryKey/lastQueryKeyRef check above never got a chance to see
    // the new roleKey because nothing here triggered the effect in the first place.
  }, [sortKey, sortDir, startDate, endDate, manualAgentIds, page, limit, columnFilters, search, roleScope, queuesDataVersion]);

  // Reset page on sort / filter / search change
  React.useEffect(() => {
    setPage(1);
  }, [sortKey, sortDir, columnFilters, search]);

  // Reset column filter state and expanded rows when date/agents change, or a poll detects
  // a real agent/queue change (e.g. a rename) — otherwise the filter dropdowns keep serving
  // cached stale names/ids even after the table itself refetches.
  React.useEffect(() => {
    colFilter.reset();
    filterParamsKeyRef.current = {};
    setExpandedRows(new Set());
    setSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, roleScope, manualAgentIds, queuesDataVersion]);

  // Params a column's cached filter options depend on: the shared time range/agent
  // scope plus every *other* column's active filter values (this column's own filter
  // isn't part of its own scoping request, per fetchFilterPage's otherFilters).
  const buildFilterParamsKey = useCallback(
    (col: string) => {
      const otherFilters = getOtherColumnFilters(columnFilters, col);
      const roleKey = `${roleScope.queueIds.join(",")}|${roleScope.agentIds.join(",")}|${roleScope.directAgentIds.join(",")}`;
      return `${startDate}|${endDate}|${roleKey}|${(manualAgentIds ?? []).join(",")}|${search}|${JSON.stringify(otherFilters)}`;
    },
    [startDate, endDate, roleScope, manualAgentIds, search, columnFilters]
  );

  // ── Column filter fetch helpers ───────────────────────────────────────────
  const fetchFilterPage = useCallback(
    async (col: string, pageIndex: number, append: boolean, dropdownSearch: string) => {
      if (!startDate || !endDate) return;
      colFilter.abortCol(col);
      const ac = new AbortController();
      colFilter.abortRefs.current[col] = ac;

      const otherFilters = getOtherColumnFilters(columnFilters, col);
      // Target column: `value` carries the dropdown's search text so the backend narrows
      // its distinct list server-side; null means no narrowing (full list).
      const filters = [...(otherFilters ?? []), { column: col, value: dropdownSearch.trim() || null }];
      const mainFilters = search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null;
      try {
        const res = await fetchDirectFilter(
          {
            startTime: startDate,
            endTime: endDate,
            roleScope,
            manualAgentIds: manualAgentIds ?? null,
            targetColumn: col,
            pageIndex,
            pageSize: FILTER_PAGE_SIZE,
            mainFilters,
            filters,
          },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        // Computed synchronously (not inside setEntry's functional updater) so hasMoreAfter
        // is reliable immediately below — the updater form only runs once React flushes the
        // state update, which can land after this function has already moved on.
        const priorOptions = append ? colFilter.getEntry(col).options : [];
        const merged = [...new Set([...priorOptions, ...res.values])];
        // Also stop when this page added nothing new: `total` is the backend's count and may never be
        // reachable by the de-duplicated list (rows vs distinct values, or a page that repeats), which
        // would leave `hasMore` stuck on and keep requesting pages on every scroll.
        const hasMoreAfter = merged.length > priorOptions.length && merged.length < res.total;
        colFilter.setEntry(col, {
          options: merged,
          hasMore: hasMoreAfter,
          loadedPage: pageIndex,
          loading: false,
        });
      } catch (e) {
        if (ac.signal.aborted) return;
        if (isAbortError(e)) return;
        colFilter.setEntry(col, { loading: false });
      }
    },
    [startDate, endDate, manualAgentIds, search, columnFilters, colFilter, roleScope]
  );

  const handleFilterOpen = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      const paramsKey = buildFilterParamsKey(col);
      if (entry.loadedPage >= 0 && filterParamsKeyRef.current[col] === paramsKey) {
        // Params unchanged since the last fetch for this column — reuse the cached options.
        return;
      }
      filterParamsKeyRef.current[col] = paramsKey;
      colFilter.setEntry(col, { loading: true, searchQuery: "", loadedPage: -1, options: [], hasMore: false });
      fetchFilterPage(col, 0, false, "");
    },
    [colFilter, fetchFilterPage, buildFilterParamsKey]
  );

  const handleFilterClose = useCallback((col: string) => {
    const timers = colFilter.searchTimers.current;
    if (timers[col]) {
      clearTimeout(timers[col]);
      delete timers[col];
    }
    // Options were narrowed by a dropdown search — invalidate the cache so the next
    // open refetches the full (unsearched) distinct list.
    if (colFilter.getEntry(col).searchQuery.trim()) {
      delete filterParamsKeyRef.current[col];
    }
  }, [colFilter]);

  const handleFilterSearchChange = useCallback((col: string, query: string) => {
    colFilter.setEntry(col, { searchQuery: query });
    const timers = colFilter.searchTimers.current;
    if (timers[col]) clearTimeout(timers[col]);
    timers[col] = setTimeout(() => {
      delete timers[col];
      // Do NOT flip loadedPage back to -1 here: ColFilterPopover reads a loaded→not-loaded
      // transition on an open popover as an external reset and calls onOpen(), which wipes the
      // search box and refetches the unfiltered list. Keep the previous options until the
      // searched response replaces them.
      colFilter.setEntry(col, { loading: true, hasMore: false });
      fetchFilterPage(col, 0, false, query);
    }, 400);
  }, [colFilter, fetchFilterPage]);

  const handleFilterLoadMore = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      if (!entry.hasMore || entry.loading) return;
      colFilter.setEntry(col, { loading: true });
      fetchFilterPage(col, entry.loadedPage + 1, true, entry.searchQuery);
    },
    [colFilter, fetchFilterPage]
  );

  const handleToggleFilterValue = useCallback((col: string, val: string) => {
    setColumnFilters((prev) => {
      const cur = prev[col] ?? [];
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      return next.length ? { ...prev, [col]: next } : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== col));
    });
  }, []);

  const handleClearFilter = useCallback((col: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((col: string, vals: string[]) => {
    if (vals.length === 0) return;
    setColumnFilters((prev) => ({ ...prev, [col]: vals }));
  }, []);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "Asc" ? "Desc" : "Asc"));
    } else {
      setSortKey(key);
      setSortDir("Asc");
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setExportLoading(true);
    try {
      // Export scopes to the filter bar's time range/agent selection only — the table's own
      // column filters are intentionally ignored, while its active sort still applies.
      const result = await fetchDirectTable({
        startTime: startDate,
        endTime: endDate,
        roleScope,
        manualAgentIds: manualAgentIds ?? null,
        sorts: sortKey ? [{ column: sortKey, direction: sortDir }] : null,
        pageIndex: 0,
        pageSize: null,
        filters: null,
      });
      const headers = EXPORT_COLUMNS.map((c) => c.title);
      const rows = result.data.map((row) =>
        EXPORT_COLUMNS.map((col) => {
          switch (col.key) {
            case "startTime":
              return convertLocalTimezone(row.startTime);
            case "voicemail":
              return row.voicemail ? "Yes" : "No";
            case "callDuration":
              return formatDuration(row.callDuration);
            case "waitingDuration":
              return formatDuration(row.waitingDuration);
            case "talkingDuration":
              return formatDuration(row.talkingDuration);
            case "direction":
              return formatDirectionForDisplay(row.direction);
            case "callStatus":
              return capitalizeFirst(row.callStatus);
            default:
              return (row as unknown as Record<string, unknown>)[col.key] ?? "";
          }
        })
      );
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws["!cols"] = EXPORT_COLUMNS.map(() => ({ wch: 15 }));
      XLSX.utils.book_append_sheet(wb, ws, "Call Journey");
      const d = new Date();
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      XLSX.writeFile(wb, `calls ${dateStr}.csv`);
    } catch (e) {
      console.error("Direct call journey export failed:", e);
    } finally {
      setExportLoading(false);
    }
  }, [startDate, endDate, manualAgentIds, sortKey, sortDir, roleScope]);


  // ── Table body ────────────────────────────────────────────────────────────
  const renderTableBody = () => {
    if (loading) {
      return skeletonRowIds.map((rowId) => (
        <div key={rowId} className="table-row h-[45px]">
          <div className="table-cell px-1" style={{ width: 32 }} />
          {columns.map((col) => (
            <div key={col.key} className="table-cell px-4 align-middle" style={{ width: col.width }}>
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
            </div>
          ))}
        </div>
      ));
    }

    return tableData.map((row, idx) => {
      const expanded = expandedRows.has(row.id);
      const cellBorder = "border-b";
      return (
        <React.Fragment key={row.id}>
          <TableGridRow index={idx} className={cellBorder}>
            <TableGridCell
              index={idx}
              sticky
              className={cn("h-[45px] px-1 border-l-0 border-r-0 border-gray-300 dark:border-0 z-20", cellBorder)}
              style={{ width: 32 }}
            >
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-action hover:text-white transition-all duration-200 flex items-center justify-center"
                onClick={() => toggleExpand(row.id)}
                aria-label={expanded ? "Collapse call journey" : "Expand call journey"}
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </TableGridCell>
            {columns.map((col) => (
              <TableGridCell
                key={col.key}
                index={idx}
                className={cn("h-[45px] text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors border-l-0 border-r-0", cellBorder)}
                style={{ width: col.width }}
              >
                <div className="text-left truncate">{getCellContent(col.key, row)}</div>
              </TableGridCell>
            ))}
          </TableGridRow>
          {expanded && (
            <tr className="table-row">
              {/* eslint-disable-next-line no-restricted-syntax -- expanded detail panel background, not zebra row/header coloring. */}
              <td className="table-cell align-middle py-2 text-muted-foreground bg-white dark:bg-[#3C5C73] dark:text-white overflow-hidden" colSpan={columns.length + 1}>
                {row.journey.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center">No journey events.</div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <Timeline events={row.journey} />
                  </div>
                )}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  // ── Cell rendering ──────────────────────────────────────────────────────────
  // Mirrors CallsDetailOverviewWidget.tsx's cell formatting (Calls tab) exactly,
  // so the Direct Call tab's Call Journey table looks and behaves consistently.
  const getCellContent = (key: string, row: DirectCallJourneyRow) => {
    switch (key) {
      case "startTime":
        return <span className="text-xs">{convertLocalTimezone(row.startTime)}</span>;
      case "direction": {
        const label = formatDirectionForDisplay(row.direction);
        return <span className={cn("text-xs", getDirectionColor(label))}>{label}</span>;
      }
      case "callStatus": {
        const label = capitalizeFirst(row.callStatus);
        return <span className={cn("text-xs", getPresenceColor(label))}>{label || "-"}</span>;
      }
      case "callDuration":
        return <span className="text-xs">{formatDuration(row.callDuration)}</span>;
      case "voicemail":
        return (
          <span className="flex w-full pl-8">
            <Checkbox
              checked={row.voicemail === true}
              tabIndex={-1}
              className="h-5 w-5 rounded-md border-2 border-gray-300 dark:border-white/40 bg-transparent data-[state=checked]:bg-brand-accent data-[state=checked]:border-[#00FEC1] data-[state=checked]:text-black pointer-events-none cursor-default"
              aria-label={row.voicemail === true ? "Call forwarded to voice mail" : "Not forwarded to voice mail"}
            />
          </span>
        );
      case "organizer":
        return (
          <TruncatedTextTooltip content={row.organizer || "-"} className="text-xs">
            {row.organizer || "-"}
          </TruncatedTextTooltip>
        );
      case "participant":
        return (
          <TruncatedTextTooltip content={row.participant || "-"} className="text-xs">
            {row.participant || "-"}
          </TruncatedTextTooltip>
        );
      case "customerName":
        return (
          <TruncatedTextTooltip content={row.customerName || "-"} className="text-xs">
            {row.customerName || "-"}
          </TruncatedTextTooltip>
        );
      case "companyName":
        return (
          <TruncatedTextTooltip content={row.companyName || "-"} className="text-xs">
            {row.companyName || "-"}
          </TruncatedTextTooltip>
        );
      default:
        return <span className="text-xs">-</span>;
    }
  };

  return (
    <Card className={TABLE_CARD_CLASS}>
      <CardHeader className="flex flex-row items-center justify-between sticky top-0 z-30 border-b">
        <div className="flex items-center gap-2">
          <WidgetTitle>Call Journey</WidgetTitle>
          <InfoTooltip content="The timeline of a call from the initial attempt to reach the callee to the moment the call is terminated within the given time." />
        </div>
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Search agent..."
            onSearch={(v) => setSearch(v.trim())}
            initialValue={search}
            debounceMs={300}
          />
          <Button
            variant="outline"
            size="sm"
            // eslint-disable-next-line no-restricted-syntax -- Export button chrome, not table row/header coloring.
            className="text-xs flex items-center gap-1 [text-decoration:none] hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#0C3451] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
            onClick={handleExport}
            disabled={!startDate || !endDate || exportLoading}
          >
            <Download className="h-3 w-3" />
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="min-w-full inline-block align-top">
            <div className="table w-full call-journey-table" style={{ borderCollapse: "collapse", borderSpacing: 0, tableLayout: "fixed" }}>
              <TableGridHeaderRow className={cn("text-xs", TABLE_HEADER_TITLE_CLASS)}>
                <TableGridHeaderCell
                  sticky
                  className={cn(TABLE_HEADER_ROW_COLOR_CLASS, "px-1 text-center transition-colors z-40 font-bold border-t-0 border-l-0 border-r-0")}
                  style={{ width: 32 }}
                />
                {columns.map((col) => (
                  <TableGridHeaderCell
                    key={col.key}
                    className="border-b border-l-0 border-r-0 border-gray-300 dark:border-0 transition-colors"
                    style={{ width: col.width }}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 text-left"
                        onClick={() => handleSort(col.key)}
                      >
                        <span className={cn("whitespace-nowrap truncate", TABLE_HEADER_TITLE_CLASS)}>{col.title}</span>
                        {sortKey === col.key && (
                          sortDir === "Asc" ? <ChevronUp className="h-3 w-3 flex-shrink-0" /> : <ChevronDown className="h-3 w-3 flex-shrink-0" />
                        )}
                      </button>
                      {col.filterable && (
                        <ColFilterPopover
                          columnKey={col.key}
                          selectedValues={columnFilters[col.key] ?? []}
                          entry={colFilter.getEntry(col.key)}
                          onOpen={() => handleFilterOpen(col.key)}
                          onClose={() => handleFilterClose(col.key)}
                          onToggleValue={handleToggleFilterValue}
                          onSearchChange={handleFilterSearchChange}
                          onLoadMore={handleFilterLoadMore}
                          onClearFilter={handleClearFilter}
                          onSelectAll={handleSelectAll}
                        />
                      )}
                    </div>
                  </TableGridHeaderCell>
                ))}
              </TableGridHeaderRow>
              <div className="table-row-group border-gray-300 dark:border-0">
                {renderTableBody()}
              </div>
            </div>
          </div>
          {!loading && (error || tableData.length === 0) && (
            <div className="py-12 text-center text-sm text-muted-foreground dark:text-white/60">
              No call records were found
            </div>
          )}
        </div>
        <TablePagingFooter
          currentPage={page}
          totalPages={Math.max(1, Math.ceil(total / limit))}
          total={total}
          pageSize={limit}
          onPageChange={setPage}
          isLoading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
        />
      </CardContent>
    </Card>
  );
};

export default DirectCallJourneyTableWidget;
